import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Option} from 'platform/components';
import {useLocale} from 'platform/locale';

import {useCallback, useMemo} from 'react';

import {isNil, isNotNil, last, length, omit} from 'ramda';

import {usePriceReport} from '../../../hooks/usePriceReport';
import {useDrillDownQuery} from '../../../store/omneticApi';
import {DrillDownLevelName} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getPermanentLevelFilters} from '../../DrillDown/components/DrillDown/utils/getPermanentLevelFilters';

const useGetCounts = <T extends string | number>(
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, T>>
) => {
  const {requestData} = usePriceReport();
  const {language} = useLocale();
  const levelFilters = getPermanentLevelFilters(omit([optionKey], filters));
  const {data: drillDownData, isLoading} = useDrillDownQuery(
    isNotNil(requestData?.market)
      ? {
          levels: [
            ...levelFilters,
            {
              level: optionKey,
              values: [],
              priority: length(levelFilters) + 1,
            },
          ],
          currency: 'CZK', // We don't know currency, but we are not going to use prices
          regions: [requestData.market],
          lang: language,
        }
      : skipToken,
    {
      skip:
        isNil(filters.make) ||
        isNil(filters.model) ||
        !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS),
    }
  );

  const counts = useMemo(
    () =>
      drillDownData?.statisticsAggregated.map((item) => ({
        option: last(item.levels)?.value,
        count: item.statistics.reduce((acc, curr) => acc + curr.amount, 0),
      })),
    [drillDownData]
  );
  return {counts, isLoading};
};

export interface FilterRelevantOptionsResult<T extends string | number> {
  options: (Option<T> & {suffix?: string})[];
  isLoading: boolean;
}

export const useFilterRelevantOptions = <T extends string | number>(
  options: Option<T>[],
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>
): FilterRelevantOptionsResult<T> => {
  const {counts, isLoading} = useGetCounts(optionKey, filters);
  const getCount = useCallback(
    (option: Option<T>) => counts?.find((count) => count.option === option.value.toString())?.count,
    [counts]
  );

  const mappedOptions = useMemo(
    () =>
      options
        .filter((item) => {
          const count = getCount(item);
          return isNotNil(count) && count > 0;
        })
        .map((item) => ({
          ...item,
          suffix: `(${getCount(item)})`,
          isDisabled: false,
        })),
    [options, getCount]
  );

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options, isLoading: false};
  }

  return {options: mappedOptions, isLoading};
};

export const useGetRelevantPowerOptions = (
  filters: Partial<Record<DrillDownLevelName, string>>
): FilterRelevantOptionsResult<string> => {
  const {counts, isLoading} = useGetCounts('power', filters);

  const options = useMemo(
    () =>
      counts
        ?.sort((a, b) => {
          const aValue = Number(a.option);
          const bValue = Number(b.option);
          return aValue - bValue;
        })
        .map((count) => ({
          value: count.option ?? '?',
          label: `${count.option} kW`,
          suffix: `(${count.count})`,
        })) ?? [],
    [counts]
  );

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options: [], isLoading: false};
  }

  return {options, isLoading};
};
